import { replaceByObject } from '@/common/utils/strings/replace.utils';
import { AlertComponent } from '@/ui/shared/components/alert/alert.component';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import _ from 'lodash';
import { from, Observable } from 'rxjs';
import { SweetAlertResult } from 'sweetalert2';

@Injectable({
    providedIn: 'root',
})
export class AlertService {
    private _coreAlertTemplate!: AlertComponent;
    public get coreAlertTemplate(): AlertComponent {
        return this._coreAlertTemplate;
    }
    public set coreAlertTemplate(v: AlertComponent) {
        this._coreAlertTemplate = v;
    }

    constructor(private readonly translate: TranslateService) {}

    private translateErrorPart(
        error: unknown,
        part: 'title' | 'message',
    ): string {
        const code = _.get(error, 'error.code', 'default');
        const key = `fe_errors_code_${code}_${part}`;
        const value = this.translate.instant(key);

        if (value && value !== key) {
            return value;
        }

        const fallbackKey = `fe_errors_code_default_${part}`;
        const fallback = this.translate.instant(fallbackKey);
        return fallback && fallback !== fallbackKey ? fallback : '';
    }

    public getErrorMessage(error: unknown): string {
        return this.translateErrorPart(error, 'message');
    }

    public getErrorTitle(error: unknown): string {
        return this.translateErrorPart(error, 'title');
    }

    public getErrorDefault(error: unknown): {
        showConfirm?: boolean;
        showCancel?: boolean;
        confirmText?: string;
        cancelText?: string;
        title?: string;
        subtitle?: string;
    } {
        return {
            showConfirm: true,
            showCancel: false,
            title: this.getErrorTitle(error),
            subtitle: this.getErrorMessage(error),
        };
    }

    public openDefault(error: unknown): Observable<SweetAlertResult> {
        return this.open(this.getErrorDefault(error));
    }

    public openScheduleUnavailable(
        type: 'pickup' | 'return',
        openHour: string,
        closeHour: string,
    ): Observable<SweetAlertResult> {
        const params = {
            branchType: type === 'pickup' ? 'recogida' : 'entrega',
            openHour,
            closeHour,
        };

        return this.open({
            showCancel: false,
            title: replaceByObject(
                this.translate.instant('fe_return_schedule_unavailable_title'),
                params,
            ),
            subtitle: replaceByObject(
                this.translate.instant('fe_return_schedule_unavailable_message'),
                params,
            ),
        });
    }

    public open(
        options: {
            showConfirm?: boolean;
            showCancel?: boolean;
            confirmText?: string;
            cancelText?: string;
            title?: string;
            subtitle?: string;
        } = {},
    ): Observable<SweetAlertResult> {
        this.coreAlertTemplate.options = options;
        this.coreAlertTemplate.options = _.merge(
            {},
            this.coreAlertTemplate.optionsDefault,
            this.coreAlertTemplate.options,
        );
        return from(this.coreAlertTemplate.alertTemplate.fire());
    }
}
